import React from 'react';
import styled from 'styled-components';
import cx from 'classnames';
import useNewColorSchemaOptions from 'utils/hooks/useNewColorSchemaOptions';
import RulerIcon from 'components/icons/Ruler';
import HomeIcon from 'components/icons/Home';
import BedIcon from 'components/icons/Bed';
import GarageIcon from 'components/icons/Garage';
import StorieIcon from 'components/icons/Storie';
import BathIcon from 'components/icons/Bath';
import HalfBathIcon from 'components/icons/HalfBath';

const Ruler = styled(RulerIcon)`
  ${({ showNewSchemaColor, color }) => showNewSchemaColor && `color: ${color};`}
`;
const Home = styled(HomeIcon)`
  ${({ showNewSchemaColor, color }) => showNewSchemaColor && `color: ${color};`}
`;
const Bed = styled(BedIcon)`
  ${({ showNewSchemaColor, color }) => showNewSchemaColor && `color: ${color};`}
`;
const Garage = styled(GarageIcon)`
  ${({ showNewSchemaColor, color }) => showNewSchemaColor && `color: ${color};`}
`;
const Storie = styled(StorieIcon)`
  ${({ showNewSchemaColor, color }) => showNewSchemaColor && `color: ${color};`}
`;
const Bath = styled(BathIcon)`
  ${({ showNewSchemaColor, color }) => showNewSchemaColor && `color: ${color};`}
`;
const HalfBath = styled(HalfBathIcon)`
  ${({ showNewSchemaColor, color }) => showNewSchemaColor && `color: ${color};`}
`;

const getRanges = (ranges) => {
  if (ranges && ranges.length > 4) {
    const r = ranges.split(' - ');
    if (r[0] === '0' && r[1] === '0') return '';
    if (r[0] === r[1]) return r[0];
  }
  return typeof ranges !== 'undefined' ? ranges : '';
};

const getHomeTypesList = (str) => {
  if (str && str.length > 0) {
    const homeTypesList = str.split('|').filter((h) => h);
    let newList = '';
    homeTypesList.forEach((h, i) => {
      if (h) {
        if (i < homeTypesList.length - 1) newList = newList + h + ' | ';
        else newList = newList + h;
      }
    });
    return newList;
  }
  return str;
};

const StatRow = ({
  IconComponent,
  label,
  value,
  showNewSchemaColor,
  newSchemaTextColorHexaCode,
  className,
  borderStyle,
}) => (
  <div
    className={cx('flex items-start gap-2.5 py-3', className)}
    style={borderStyle}
  >
    <IconComponent
      className={cx('h-6 w-6 flex-shrink-0 mt-0.5', {
        'text-mattamy-blue': !showNewSchemaColor,
      })}
      showNewSchemaColor={showNewSchemaColor}
      color={newSchemaTextColorHexaCode}
    />
    <div className='flex flex-col gap-1'>
      <span
        className={cx('font-graphie text-xs font-semibold', {
          'text-mattamy-blue': !showNewSchemaColor,
        })}
        style={
          showNewSchemaColor ? { color: newSchemaTextColorHexaCode } : undefined
        }
      >
        {label}
      </span>
      <span
        className={cx('font-graphie text-base font-medium text-sm', {
          'text-mattamy-gray': !showNewSchemaColor,
        })}
        style={
          showNewSchemaColor ? { color: newSchemaTextColorHexaCode } : undefined
        }
      >
        {value}
      </span>
    </div>
  </div>
);

const CommunityProductOverviewHomeDetails = ({ homeDetails }) => {
  const { newSchemaTextColorHexaCode, showNewSchemaColor } =
    useNewColorSchemaOptions();

  const dividerColor = showNewSchemaColor
    ? newSchemaTextColorHexaCode
    : '#d8dde4';

  const homeTypesValue = getHomeTypesList(homeDetails && homeDetails.homeTypes);
  const showHomeTypes = !!(homeDetails && homeDetails.homeTypes);

  const stats = homeDetails
    ? [
        {
          key: 'bedrooms',
          IconComponent: Bed,
          label: 'Bedrooms',
          value: getRanges(homeDetails.bedroomRanges),
        },
        {
          key: 'fullBaths',
          IconComponent: Bath,
          label: 'Full Bathrooms',
          value: getRanges(homeDetails.fullBathRanges),
        },
        {
          key: 'halfBaths',
          IconComponent: HalfBath,
          label: 'Half Bathrooms',
          value: getRanges(homeDetails.halfBathRanges),
        },
        {
          key: 'sqft',
          IconComponent: Ruler,
          label: 'Sq. Ft.',
          value: getRanges(homeDetails.sqFtRanges),
        },
        {
          key: 'stories',
          IconComponent: Storie,
          label: 'Stories',
          value: getRanges(homeDetails.storyRanges),
        },
        {
          key: 'garages',
          IconComponent: Garage,
          label: 'Garages',
          value: getRanges(homeDetails.garageRanges),
        },
      ].filter((s) => s.value !== '')
    : [];

  return (
    <div className='w-full md:pl-10 mt-10 mb-0 md:mb-8 '>
      <p
        className={cx(
          'font-trade-gothic-20 text-base font-semibold uppercase mb-4 tracking-wide',
          { 'text-mattamy-blue': !showNewSchemaColor },
        )}
        style={
          showNewSchemaColor ? { color: newSchemaTextColorHexaCode } : undefined
        }
      >
        Home Details
      </p>

      {showHomeTypes && (
        <StatRow
          IconComponent={Home}
          label='Home Types'
          value={homeTypesValue}
          showNewSchemaColor={showNewSchemaColor}
          newSchemaTextColorHexaCode={newSchemaTextColorHexaCode}
          className='pb-3.5'
          borderStyle={{ borderBottom: `1px solid ${dividerColor}` }}
        />
      )}

      <div className='grid grid-cols-2 gap-0'>
        {stats.map((stat, i) => {
          const isLeftCol = i % 2 === 0;
          const isLastPair = i >= stats.length - 2;
          const borderStyle = {
            borderBottom: isLastPair ? 'none' : `1px solid ${dividerColor}`,
            ...(isLeftCol
              ? {
                  paddingRight: 16,
                  borderRight: `1px solid ${dividerColor}`,
                }
              : { paddingLeft: 16 }),
          };
          return (
            <StatRow
              key={stat.key}
              IconComponent={stat.IconComponent}
              label={stat.label}
              value={stat.value}
              showNewSchemaColor={showNewSchemaColor}
              newSchemaTextColorHexaCode={newSchemaTextColorHexaCode}
              borderStyle={borderStyle}
            />
          );
        })}
      </div>
    </div>
  );
};

export default CommunityProductOverviewHomeDetails;
